import React, { useState, useEffect } from 'react';
import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import { apiPost, apiGet } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

// Helper function to ensure valid JSON object
const ensureValidJsonObject = (data) => {
  try {
    // If it's already an object and not null
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      return data;
    }
    
    // If it's a string, try to parse it
    if (typeof data === 'string') {
      return JSON.parse(data);
    }
    
    // Default fallback
    return { query: { match_all: {} } };
  } catch (e) {
    console.error('Error ensuring valid JSON object:', e);
    return { query: { match_all: {} } };
  }
};

export default function E2DSL() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [indices, setIndices] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState('');
  const [indexMapping, setIndexMapping] = useState(null);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [queryResults, setQueryResults] = useState({ query: { match_all: {} } });
  const [isExecuting, setIsExecuting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dslQuery, setDslQuery] = useState({ query: { match_all: {} } });
  const [toast, setToast] = useState({ message: '', show: false, type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ message, show: true, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  };

  const copyToClipboard = (jsonString) => {
    navigator.clipboard.writeText(jsonString)
      .then(() => showToast('JSON copied to clipboard'))
      .catch(() => showToast('Failed to copy JSON', 'error'));
  };

  useEffect(() => {
    fetchIndices();
  }, []);

  useEffect(() => {
    if (selectedIndex) {
      fetchMapping(selectedIndex);
    }
  }, [selectedIndex]);

  const fetchIndices = async () => {
    try {
      const response = await apiGet('/e2dsl/indices');
      // Filter indices to only show ones starting with "opensearch_dashboards_sample"
      const filteredIndices = response.filter(idx => idx.index.startsWith('opensearch_dashboards_sample'));
      setIndices(filteredIndices);
    } catch (err) {
      setError('Failed to fetch indices');
      console.error('Error fetching indices:', err);
    }
  };

  const fetchMapping = async (indexName) => {
    try {
      console.log('Fetching mapping for index:', indexName);
      const mapping = await apiGet(`/e2dsl/mapping/${indexName}`);
      setIndexMapping(mapping);
      console.log('Retrieved mapping:', mapping);
    } catch (err) {
      setError('Failed to fetch index mapping');
      console.error('Error fetching mapping:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !selectedIndex) return;

    const userMessage = {
      role: "user",
      content: [{ text: inputMessage }]
    };

    setIsGenerating(true);
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputMessage('');

    try {
      const response = await apiPost('/e2dsl/generate', {
        messages: [...messages, userMessage],
        indexName: selectedIndex
      });

      setMessages(prevMessages => [...prevMessages, response]);
    } catch (err) {
      setError('Failed to generate query');
      console.error('Error generating query:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleIndexChange = (e) => {
    const newIndex = e.target.value;
    setSelectedIndex(newIndex);
    setInputMessage(''); // Clear input when index changes
  };

  const executeQuery = async (jsonString) => {
    try {
      // First show the slider with empty results
      setQueryResults(null);
      setShowResults(true);
      // Wait for slider animation to complete (500ms transition + 100ms buffer)
      await new Promise(resolve => setTimeout(resolve, 600));
      
      setIsExecuting(true);
      const parsedResponse = JSON.parse(jsonString);
      const query = parsedResponse;
      
      console.log('Executing query:', query);
      
      const results = await apiPost('/e2dsl/execute', {
        query,
        indexName: selectedIndex
      });

      setQueryResults(results);
      console.log('Query results:', results);
    } catch (err) {
      setError('Failed to execute query: ' + err.message);
      console.error('Error executing query:', err);
      setShowResults(false);
    } finally {
      setIsExecuting(false);
    }
  };

  const closeSlider = () => {
    setShowResults(false);
    // Clear results after slider animation
    setTimeout(() => setQueryResults(null), 500);
  };

  const ResultsSlider = () => (
    <div className={`results-slider ${showResults ? 'show' : ''}`}>
      <div className="slider-header">
        <h3>Query Results</h3>
        <button 
          className="close-button" 
          onClick={closeSlider}
        >×</button>
      </div>
      <div className="slider-body">
        {isExecuting ? (
          <div className="loading">
            <div className="progress-spinner"></div>
            <span>Executing query...</span>
          </div>
        ) : (
          queryResults && (
            <div className="results-content">
              <JsonView 
                src={queryResults}
                theme="light"
                collapsed={false}
                displayDataTypes={false}
                quotesOnKeys={false}
                displayArrayKey={false}
                enableClipboard={false}
                style={{
                  backgroundColor: 'transparent',
                  fontFamily: 'Monaco, monospace',
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}
              />
            </div>
          )
        )}
      </div>
    </div>
  );

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    const messageContent = message.content[0].text;
    
    // Check if the message contains a code block
    const codeBlockMatch = messageContent.match(/```json\n([\s\S]*?)\n```/);
    
    return (
      <div key={index} className={`message ${isUser ? 'user' : 'system'}`}>
        {codeBlockMatch ? (
          <>
            <p>{messageContent.split('```json')[0]}</p>
            <div className="json-container">
              <div className="json-header">
                <span>Query DSL</span>
                <div className="json-actions">
                  <button 
                    className="action-button"
                    onClick={() => copyToClipboard(codeBlockMatch[1])}
                    disabled={isExecuting}
                  >
                    Copy JSON
                  </button>
                  <button 
                    className="action-button execute-button"
                    onClick={() => executeQuery(codeBlockMatch[1])}
                    disabled={isExecuting}
                  >
                    {isExecuting ? 'Executing...' : 'Execute Query'}
                  </button>
                </div>
              </div>
              <div style={{ 
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '4px'
              }}>
                <JsonView 
                  src={JSON.parse(codeBlockMatch[1])}
                  theme="light"
                  collapsed={false}
                  displayDataTypes={false}
                  quotesOnKeys={false}
                  displayArrayKey={false}
                  enableClipboard={false}
                  style={{
                    backgroundColor: 'transparent',
                    fontFamily: 'Monaco, monospace',
                    fontSize: '14px',
                    lineHeight: '1.4'
                  }}
                />
              </div>
            </div>
            <p>{messageContent.split('```')[2]}</p>
          </>
        ) : (
          <p>{messageContent}</p>
        )}
        <small className="message-timestamp">
          {message.role}
        </small>
      </div>
    );
  };

  const clearConversation = () => {
    setMessages([]);
    setInputMessage('');
    setError(null);
    setShowResults(false);
    setQueryResults(null);
    setIsGenerating(false);
    setIsExecuting(false);
    showToast('Conversation cleared');
  };

  return (
    <div className="e2dsl-container">
      <button 
        className="clear-chat" 
        onClick={clearConversation}
        aria-label="Clear conversation"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M3 6h18"></path>
          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
        </svg>
        <span className="clear-chat-tooltip">Clear conversation</span>
      </button>

      <div className="chat-container">
        {messages.map((message, index) => renderMessage(message, index))}
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {toast.show && (
        <div className={`toast show ${toast.type}`}>
          {toast.message}
        </div>
      )}

      {isGenerating && (
        <div className="progress-indicator">
          <div className="progress-spinner"></div>
          <span>Generating query...</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="input-form">
        <select 
          value={selectedIndex}
          onChange={handleIndexChange}
          className="index-select"
          disabled={isGenerating}
        >
          <option value="">Select an index</option>
          {indices.map((idx) => (
            <option key={idx.index} value={idx.index}>
              {idx.index} ({idx['docs.count']} docs)
            </option>
          ))}
        </select>
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder={selectedIndex ? "Type your query in English..." : "Select an index to start querying in English"}
          className="message-input"
          disabled={isGenerating || !selectedIndex}
        />
        <button 
          type="submit" 
          className="send-button" 
          disabled={isGenerating || !selectedIndex || !inputMessage.trim()}
        >
          {isGenerating ? 'Generating...' : 'Generate DSL'}
        </button>
      </form>

      <ResultsSlider />
    </div>
  );
}
